import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Breakpoints } from '@rd-lib/breakpoints';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { TCV2_CHECKOUT_LINKED_ACCOUNT_DIALOG } from '@rd/core/tokens/transfer-connect-v2.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { LoyaltyProgram, Membership } from '@rd/types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CheckoutLinkedAccountPosition,
  TransferConnectV2SettingService,
} from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

type ComponentRenderState = 'loading' | 'linked' | 'unlinked';

@Component({
  selector: 'rd-tcv2-checkout-linked-account',
  templateUrl: './checkout-linked-account.component.html',
  styleUrls: ['./checkout-linked-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2CheckoutLinkedAccountComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-tcv2-checkout-linked-account') componentClass = true;
  @Input() isLoggedIn: boolean;
  @Input() linkedAccount: Membership;
  @Input() loading: boolean;
  @Input() loyaltyProgram: LoyaltyProgram;

  isMobileViewport: boolean;
  linkedAccountPosition: CheckoutLinkedAccountPosition;
  shouldRenderLinkAccountButtonIcon: boolean;

  private destroy$ = new Subject();

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  private tcv2CheckoutLinkedAccountDialogComponentType = inject(
    TCV2_CHECKOUT_LINKED_ACCOUNT_DIALOG,
  );

  constructor(
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private iconRegistryService: IconRegistryService,
    private translateService: TranslateService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
    private windowResizeService: WindowResizeService,
  ) {}

  ngOnInit(): void {
    this.linkedAccountPosition =
      this.transferConnectV2SettingService.linkedAccountPosition;
    this.shouldRenderLinkAccountButtonIcon =
      this.transferConnectV2SettingService.shouldRenderLinkAccountButtonIcon;
    if (this.shouldRenderLinkAccountButtonIcon) {
      this.iconRegistryService.registerIcon('arrow-right');
    }
    this.detectViewportSize();

    this.windowResizeService.windowResizeObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.detectViewportSize());
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  componentRenderState(): ComponentRenderState {
    if (this.loading) {
      return 'loading';
    }

    if (!!this.linkedAccount) {
      return 'linked';
    } else {
      return 'unlinked';
    }
  }

  openLinkAccountDialog(): void {
    const screenMode = this.isMobileViewport ? 'fullscreen' : 'center-md';

    if (this.isLoggedIn) {
      this.dialogService.open(
        this.tcv2CheckoutLinkedAccountDialogComponentType,
        {
          id: 'tcv2-checkout-link-account-dialog',
          data: {
            loyaltyProgram: this.loyaltyProgram,
            screenMode,
          },
          ariaLabel: this.translateService.instant(
            'transfer_v2.link_account_dialog.title',
            { loyaltyProgramShortName: this.loyaltyProgram.shortName },
          ),
          screenMode,
        },
      );
    } else {
      this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
        id: 'auth-secure-route-dialog',
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'accessibility.core.auth_dialog_aria_label',
        ),
        keepModalOnRouteChange: true,
      });
    }
  }

  get shouldRenderLinkedAccountOnTop(): boolean {
    return this.linkedAccountPosition === 'top';
  }

  private detectViewportSize(): void {
    const currentIsMobileViewport = this.isMobileViewport;
    this.isMobileViewport = window.innerWidth < Breakpoints.DESKTOP;

    if (this.isMobileViewport !== currentIsMobileViewport) {
      this.cdRef.detectChanges();
    }
  }
}
